import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { logError } from '@/lib/sentry';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Sentry 에러 바운더리 컴포넌트
 * React 컴포넌트에서 발생한 오류를 캡처하고 Sentry로 전송
 */
export const SentryErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  // 컴포넌트 마운트 시 에러 바운더리가 활성화되었음을 콘솔에 로깅
  useEffect(() => {
    console.log('[Sentry] 에러 바운더리가 활성화되었습니다.');
  }, []);

  // 에러 발생 시 표시할 fallback UI
  const ErrorFallback = ({ resetError }: { resetError: () => void }) => (
    <div className="flex flex-col items-center justify-center p-6 text-center">
      <h2 className="text-xl font-semibold text-red-600 mb-2">
        오류가 발생했습니다
      </h2>
      <p className="text-gray-600 mb-4">
        문제가 지속되면 고객센터로 문의해주세요.
      </p>
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        onClick={() => {
          console.log('[Sentry] 에러 상태 초기화 중...');
          resetError();
          window.location.reload();
        }}
      >
        페이지 새로고침
      </button>
    </div>
  );

  return (
    <Sentry.ErrorBoundary
      fallback={ErrorFallback}
      beforeCapture={(scope) => {
        scope.setLevel("error");
        scope.setTag("handled", "error-boundary");
        console.log('[Sentry] 에러 캡처 전처리');
      }}
      onError={(error) => {
        logError('[Sentry] 에러 발생:', error);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

/**
 * 고차 컴포넌트(HOC) - 에러 바운더리를 적용
 * 개별 컴포넌트에 Sentry 에러 바운더리를 적용할 때 사용
 */
export const withSentryErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const WithErrorBoundary: React.FC<P> = (props) => (
    <SentryErrorBoundary>
      <Component {...props} />
    </SentryErrorBoundary>
  );
  
  const displayName = Component.displayName || Component.name || 'Component';
  WithErrorBoundary.displayName = `withSentryErrorBoundary(${displayName})`;
  
  return WithErrorBoundary;
};

export default SentryErrorBoundary; 